import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Alert, Box, Button, Paper, Tooltip } from '@mui/material';

interface DataTableProps<T extends { id: number | string }> {
  rows: T[];
  columns: GridColDef[];
  loading?: boolean;
  rowCount?: number;
  paginationMode?: 'server' | 'client';
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (m: GridPaginationModel) => void;
  errorMessage?: string;
  emptyMessage?: string;
  /**
   * Explicit fixed height. When omitted the table fills its flex parent
   * (recommended) — host pages should wrap it in a flex column so the
   * grid stretches to the available viewport height.
   */
  height?: number | string;
  /**
   * Hide the toolbar (column filters, quick search, density, columns,
   * export). Default is to show it so users can search/filter every list.
   */
  disableToolbar?: boolean;
}

/**
 * Toolbar shown above every list table. Reproduces the buttons MUI's
 * `<GridToolbar>` ships (Columns / Filters / Density / Export) plus a
 * disabled IMPORT button — placeholder for the bulk-upload feature that
 * isn't built yet. Quick-search sits at the right edge, debounced so each
 * keystroke doesn't refilter immediately.
 *
 * Lives next to the DataGrid (not exported) so every page that uses
 * `<DataTable>` gets the new button for free.
 */
function DataTableToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Tooltip title="Import — coming soon">
        {/* `span` wrapper is needed because Tooltip can't render a tooltip
            on a disabled button (pointer events are off on the button). */}
        <span>
          <Button
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            disabled
            aria-label="import (disabled)"
          >
            Import
          </Button>
        </span>
      </Tooltip>
      <Box sx={{ flex: 1 }} />
      <GridToolbarQuickFilter debounceMs={250} />
    </GridToolbarContainer>
  );
}

/**
 * Standard table for resource list views. Wraps `<DataGrid>` with a paper
 * container, error/empty states, the toolbar above, and a tuned default
 * height so callers don't have to repeat boilerplate per page.
 *
 * Filtering and the quick-search box operate on the **currently loaded
 * rows** — since most list pages use server-side pagination, that's the
 * current page only. Acceptable for the typical small admin datasets.
 */
export function DataTable<T extends { id: number | string }>({
  rows,
  columns,
  loading,
  rowCount,
  paginationMode = 'server',
  paginationModel,
  onPaginationModelChange,
  errorMessage,
  emptyMessage = 'No records found.',
  height,
  disableToolbar,
}: DataTableProps<T>) {
  // When the caller doesn't pin a height, the paper + the inner grid box
  // claim the remaining vertical space of the parent flex column so the
  // table never bottoms out at a stubby 540 px on tall viewports.
  const flexFill = height === undefined;
  return (
    <Paper
      sx={{
        width: '100%',
        ...(flexFill && {
          flex: 1,
          minHeight: 320,
          display: 'flex',
          flexDirection: 'column',
        }),
      }}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ m: 2 }}>{errorMessage}</Alert>
      )}
      <Box
        sx={
          flexFill
            ? { flex: 1, minHeight: 0, width: '100%' }
            : { height, width: '100%' }
        }
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationMode={paginationMode}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          // Use `initialState` (not the `density` prop) — the latter is a
          // controlled value and would override the toolbar's density toggle
          // so users couldn't switch compact/comfortable.
          initialState={{ density: 'standard' }}
          slots={{
            toolbar: disableToolbar ? undefined : DataTableToolbar,
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                {loading ? 'Loading…' : emptyMessage}
              </Box>
            ),
          }}
          sx={{ border: 0 }}
        />
      </Box>
    </Paper>
  );
}
